// native-voice.ts — Brücke zur nativen App-Hülle (Capacitor), Phase 1 Task 1.1
//
// Diese Datei lebt im Web-Bundle (voice.ssi.at) und läuft in ZWEI Umgebungen:
//   • normaler Browser (PWA)  → `isNativePlatform()` === false
//   • native Capacitor-WebView → `isNativePlatform()` === true
//
// Sie erkennt die Hülle, reicht ihre Plugins heraus und spricht das Plugin
// `PawbotVoice` für das Wach-Wort an. Die Gesprächs-Verbindung selbst
// (`createVoiceClient`, WebRTC oder natives Plugin) steht in voice-client.ts.
//
// ⚠️ KEIN Import von webrtc.ts/livekit-client hier (01.11.2026, E2): /neun und
//    der Playground brauchen diese Brücke, aber nie LiveKit; die Kette über
//    diese Datei zog sonst rund 487 KB in neun.js und testballon.js.
//
// WICHTIG: KEIN statischer `import` von "@capacitor/core" — das Web-Projekt hat
// diese Abhängigkeit nicht, und das Bündel muss auch im reinen Browser bauen.
// Capacitor injiziert in der nativen WebView ein `window.Capacitor`-Global; nur
// darüber greifen wir lazy zu (Zugriff erst zur Laufzeit, kein Top-Level-Effekt).

// Ticket 2536: das Wach-Wort-Ereignis wird an EINER Stelle gelesen.
import { wachwortEreignisLesen, type WachwortEreignis } from "./wachwort-satz.js";

// ---- Native-Plugin-Schnittstelle (Spiegel von ~/voice-v8-native/src/pawbot-voice.ts) ----
// Hier dupliziert, weil das Native-Projekt ein separates Repo ist und das
// Web-Bundle nicht daraus importieren kann. Eine Quelle bleibt der Plan/Spec.
export interface PawbotVoicePlugin {
  /**
   * `callId` (Ticket 2728, zweite Opus-Prüfung K1): die Kennung eines Anrufs von
   * Vespera, für den dieses Gespräch aufgebaut wird. Die App sperrt ein connect
   * nur für eine callId, deren Anruf vorzeitig beendet wurde (aufgelegt, bevor
   * das Gespräch stand) — gewöhnliche Anrufe tragen keine und bleiben frei.
   * Ältere Apps ignorieren das Feld.
   */
  connect(opts: { token: string; url: string; callId?: string }): Promise<void>;
  /**
   * `auflegen` (Ticket 2728, 02.10.2026): Martin legt auf — ein Anruf von
   * Vespera endet in CallKit SOFORT. Ohne das Feld (Neuaufbau) wartet die App
   * 12 s, ob das Gespräch gleich wieder aufgebaut wird; vorher blieb der
   * Anruf-Bildschirm nach „Auflegen“ so lange stehen. Ältere Apps ignorieren es.
   */
  disconnect(opts?: { auflegen?: boolean }): Promise<void>;
  setMicEnabled(opts: { enabled: boolean }): Promise<void>;
  setBotAudioMuted(opts: { muted: boolean }): Promise<void>;
  /** Publiziert eine Nachricht über den LiveKit-Data-Channel (gleicher Draht wie webrtc.ts). */
  sendData(opts: {
    data: string;
    topic?: string;
    reliable?: boolean;
  }): Promise<void>;
  /**
   * Ticket 2503: das Wach-Wort „Hey Vespera".
   *
   * ⚠️ Schaltet nur den WUNSCH. Wann wirklich gelauscht wird, entscheidet
   *    der native Lauscher selbst — er ist der rangniedrigste
   *    Mikrofon-Nutzer und tritt beiseite, sobald Tagesaufnahme,
   *    Sprechtaste, Anruf oder Wiedergabe den Ton haben.
   */
  wachwortSetzen(opts: { an: boolean }): Promise<{ an: boolean }>;
  wachwortZustand(): Promise<{ an: boolean }>;
  /** Ab Build 67: die Seite meldet, ob sie ein Mikrofon haelt. */
  wachwortWebMikro?(opts: { an: boolean }): Promise<void>;
  /**
   * Ab Build 106: die Kippe „Mikro beim Vorlesen offen“ (UserDefaults, nativ Standard AN). Ohne `an` nur lesen.
   * ⚠️ Liegt nativ NUR auf „PawbotVoice“ (PawbotVoicePlugin.swift), nicht auf „PawbotRecorder“ — bis 17.10.2026 rief
   *    die Seite sie dort, der Aufruf scheiterte still und der native Standard AN galt überall.
   */
  mikroBeimVorlesenSetzen?(opts: { an?: boolean }): Promise<{ an?: unknown } | undefined>;
  /** Ab Build 110: lokale Mitteilung „Vespera hat geantwortet“ (nur hinten, kurz nach einem Wach-Wort-Treffer). */
  wachwortAntwort?(opts: { text: string }): Promise<void>;
  /**
   * 20.10.2026: nennt der Text, den Vespera gerade/gleich spricht, ihren Namen? NUR das Bool, nie Text
   * (Echo-Schutz des Apple-Zeugen beim lockeren Wach-Wort). Ältere Builds kennen die Methode nicht.
   */
  wachwortWiedergabeName?(opts: { enthaelt: boolean }): Promise<void>;
  /** Ab Build 71: Absturzberichte vom letzten Lauf (einmal abgeholt, weg). */
  absturzBerichte?(): Promise<{ berichte?: string[] }>;
  addListener(
    event: PawbotVoiceEvent,
    cb: (data: unknown) => void,
  ): Promise<PluginListenerHandle>;
}
export type PawbotVoiceEvent =
  | "stateChanged"
  | "dataReceived"
  | "transcription"
  | "reconnecting"
  | "reconnected"
  | "audioLevel"
  /**
   * Ticket 2503: „Hey Vespera" ist gefallen. Ab Build 77 (Ticket 2536) mit
   * dem Satz danach: `satz`, `ton` (base64), `mime`, `dauerMs`.
   */
  | "wachwort"
  /** Ticket 2503 (Build 67): ein Zustandswechsel des Lauschers. */
  | "wachwortSpur"
  | "error";
export interface PluginListenerHandle {
  remove: () => void;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  registerPlugin?: <T>(name: string) => T;
  Plugins?: Record<string, unknown>;
}

function capacitor(): CapacitorGlobal | null {
  const c = (globalThis as unknown as { Capacitor?: unknown }).Capacitor;
  return c && typeof c === "object" ? (c) : null;
}

/** True nur in der nativen Capacitor-Hülle; im Browser immer false. */
export function isNativePlatform(): boolean {
  const c = capacitor();
  return !!(c?.isNativePlatform?.());
}

/**
 * In der nativen Hülle füllt die WebView den ganzen Bildschirm (auch unter
 * Statusleiste/Notch). Ohne `viewport-fit=cover` liefert `env(safe-area-inset-*)`
 * 0 → der Header klebt unter der Statusleiste. Wir aktivieren viewport-fit=cover
 * NUR nativ (die Standalone-PWA bleibt bewusst ohne — sonst Footer-Spalt, siehe
 * index.html Pre.134); die bestehende .topbar/.bottom-nav-Safe-Area-CSS greift dann.
 */
export function applyNativeViewportFit(): void {
  if (!isNativePlatform()) return;
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const content = meta.getAttribute("content") ?? "";
  if (/viewport-fit/.test(content)) return;
  meta.setAttribute("content", `${content},viewport-fit=cover`);
}

/**
 * Kennt die INSTALLIERTE App dieses Plugin (und diese Methode)? (24.09.2026,
 * Build 77, Tickets 2547/2396)
 *
 * ⚠️ Die Oberfläche kommt vom Server, die App aus TestFlight — eine neue
 *    Seite trifft also auch auf ältere App-Fassungen. `registerPlugin` gibt
 *    immer einen Stellvertreter zurück; erst der Aufruf scheitert
 *    („not implemented"). Was die App wirklich mitbringt, meldet sie beim
 *    Start in `Capacitor.PluginHeaders`.
 */
export function nativesPluginKennt(plugin: string, methode?: string): boolean {
  if (!isNativePlatform()) return false;
  const kopf = (capacitor() as { PluginHeaders?: unknown } | null)?.PluginHeaders;
  if (!Array.isArray(kopf)) return false;
  const p = (kopf as Array<{ name?: string; methods?: Array<{ name?: string }> }>).find(
    (k) => k?.name === plugin,
  );
  if (!p) return false;
  return methode === undefined || !!p.methods?.some((m) => m?.name === methode);
}

/** Holt ein registriertes Capacitor-Plugin-Proxy über das `window.Capacitor`-Global
 *  (kein statischer @capacitor/core-Import). Geteilt von allen nativen Adaptern. */
export function getCapacitorPlugin<T>(name: string): T | null {
  const c = capacitor();
  if (!c) return null;
  if (typeof c.registerPlugin === "function") return c.registerPlugin<T>(name);
  if (c.Plugins?.[name]) return c.Plugins[name] as T;
  return null;
}

/** Das Plugin „PawbotVoice“ der App-Hülle (Gespräch und Wach-Wort), im Browser null. */
export function pawbotVoicePlugin(): PawbotVoicePlugin | null {
  return getCapacitorPlugin<PawbotVoicePlugin>("PawbotVoice");
}

/**
 * Wartet bis `maxMs` auf ein Plugin. Beim Kaltstart der App kann `window.Capacitor` beim Start der Seite noch fehlen
 * (Ticket 2754) — ohne Warten ginge genau die erste Meldung verloren. Im Browser kommt nach `maxMs` null.
 */
async function wartePlugin<T>(name: string, maxMs = 1000): Promise<T | null> {
  const bis = Date.now() + maxMs;
  for (;;) {
    const p = getCapacitorPlugin<T>(name);
    if (p) return p;
    if (Date.now() >= bis) return null;
    await new Promise((r) => setTimeout(r, 50));
  }
}


/**
 * Das Wach-Wort an- oder abschalten. (22.09.2026, Ticket 2503)
 *
 * Gibt zurueck, ob es danach wirklich an ist — die Erlaubnis kann fehlen,
 * und dann bleibt es aus, ohne dass jemand geraten haette.
 *
 * ⚠️ Nur in der nativen Huelle. Ein Browser-Tab bekommt im Hintergrund kein
 *    Mikrofon; ein Wach-Wort, das nur bei offener Seite hoert, waere ein
 *    Versprechen, das es nicht halten kann.
 */
export async function wachwortSetzen(an: boolean): Promise<boolean> {
  const p = pawbotVoicePlugin();
  if (!p?.wachwortSetzen) return false;
  try {
    const r = await p.wachwortSetzen({ an });
    return r?.an === true;
  } catch {
    return false;
  }
}

/**
 * Die Zustandswechsel des Lauschers mitlesen — „lauscht", „wartet: …",
 * „Motor startet nicht: …". (23.09.2026, Ticket 2503)
 *
 * ⚠️ Bis Build 66 schrieb der Lauscher nur ins Geraeteprotokoll. Martin
 *    meldete zweimal „funktioniert nicht", und der Server hatte keine einzige
 *    Zeile dazu. Aeltere Builds kennen das Ereignis nicht — dann kommt hier
 *    einfach nichts an.
 */
export async function aufWachwortSpurHoeren(
  cb: (zeile: string) => void,
): Promise<(() => void) | null> {
  const p = pawbotVoicePlugin();
  if (!p?.addListener) return null;
  try {
    const h = await p.addListener("wachwortSpur", (d) => {
      const zeile =
        d && typeof d === "object" && "zeile" in d
          ? String((d).zeile).slice(0, 200)
          : "";
      if (zeile) cb(zeile);
    });
    return () => h.remove();
  } catch {
    return null;
  }
}

/**
 * Die native Kippe „Mikro beim Vorlesen offen“ stellen (Plugin „PawbotVoice“, ab Build 106). Wirft nie.
 * Gibt den Stand zurück, den die App danach meldet, oder `fehler`: `kein-plugin` (Browser/keine Hülle) bzw. der Grund,
 * warum die App den Aufruf abgelehnt hat (z. B. älterer Build ohne die Methode: „… is not implemented …“).
 */
export async function mikroBeimVorlesenSetzen(an: boolean): Promise<{ an: boolean } | { fehler: string }> {
  const p = await wartePlugin<PawbotVoicePlugin>("PawbotVoice");
  if (!p || !isNativePlatform()) return { fehler: "kein-plugin" };
  if (typeof p.mikroBeimVorlesenSetzen !== "function") return { fehler: "methode-fehlt" };
  try {
    const r = await p.mikroBeimVorlesenSetzen({ an });
    return { an: typeof r?.an === "boolean" ? r.an : an };
  } catch (e) {
    const text = e instanceof Error ? e.message : String(e);
    return { fehler: text.replace(/\s+/g, " ").slice(0, 120) || "unbekannt" };
  }
}

/**
 * Build 110: der App sagen, dass die Antwort auf ein Wach-Wort da ist (Vorschau für die Mitteilung, ≤ 80 Zeichen).
 * Wirft nie; ohne Plugin oder in einem älteren Build passiert nichts. Gibt zurück, ob die App den Aufruf angenommen hat.
 */
export async function wachwortAntwort(text: string): Promise<boolean> {
  const p = pawbotVoicePlugin();
  if (!p?.wachwortAntwort) return false;
  try {
    await p.wachwortAntwort({ text });
    return true;
  } catch {
    return false;
  }
}

/**
 * Der App melden, ob der gesprochene Text den Namen nennt (nur ein Bool, kein Text). Wirft nie, loggt nie;
 * ohne Plugin oder in einem älteren Build passiert nichts.
 */
export async function wachwortWiedergabeName(enthaelt: boolean): Promise<void> {
  const p = pawbotVoicePlugin();
  if (!p?.wachwortWiedergabeName) return;
  try {
    await p.wachwortWiedergabeName({ enthaelt });
  } catch {
    /* aelterer Build */
  }
}

/**
 * Dem Lauscher sagen, ob die Seite ein Mikrofon haelt. (23.09.2026)
 * Aeltere Builds kennen die Methode nicht — dann bleibt es still.
 */
export async function wachwortWebMikro(an: boolean): Promise<void> {
  const p = pawbotVoicePlugin();
  if (!p?.wachwortWebMikro) return;
  try {
    await p.wachwortWebMikro({ an });
  } catch {
    /* aelterer Build */
  }
}

/**
 * Was beim letzten Lauf abgestuerzt ist. (23.09.2026) Aeltere Builds kennen
 * die Methode nicht — dann gibt es nichts zu melden.
 */
export async function absturzBerichte(): Promise<string[]> {
  const p = pawbotVoicePlugin();
  if (!p?.absturzBerichte) return [];
  try {
    const r = await p.absturzBerichte();
    return Array.isArray(r?.berichte) ? r.berichte.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Auf „Hey Vespera" hoeren. Gibt einen Abmelder zurueck, oder null.
 *
 * 24.09.2026 (Ticket 2536): Ab Build 77 traegt das Ereignis den Satz nach dem
 * Wach-Wort (Text + Ton); aeltere Builds nur den Anlass — dann ist `satz`
 * null. Gelesen wird an EINER Stelle (lib/wachwort-satz.ts).
 */
export async function aufWachwortHoeren(
  cb: (ereignis: WachwortEreignis) => void,
): Promise<(() => void) | null> {
  const p = pawbotVoicePlugin();
  if (!p?.addListener) return null;
  try {
    const h = await p.addListener("wachwort", (d) => {
      cb(wachwortEreignisLesen(d));
    });
    return () => h.remove();
  } catch {
    return null;
  }
}
